import { Form, Modal, Space, Spin, Table, Tooltip, notification } from "antd";
import React, { useState } from "react";
import { SystemMgmtService } from "../../../../services/systemMgmt/systemMgmt.service";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateNotification } from "../../../../components/notifier";
import { HiOutlineTrash, HiPencilSquare } from "react-icons/hi2";
import { Button } from "react-bootstrap";
import { InsertSoftwareForm } from "./insert-form";
import { ISoftware, eSystemType } from "../../../../models/ISoftware";
import { systemTeams } from "../../../../constants";
import UploadExcelFile from "../../../../components/input/arrayData/upload-excel-file";
import DownloadExcelTemplate from "../../../../components/input/arrayData/DownloadExcelTemplate";
import { StringHelper } from "../../../../helper";
import { useColumn } from "../Employees/hooks/use-column";
import { useCheckPermission } from "../Permission/hooks/use-check-permission";
import { configPermission } from "../../../../constants/configPermission";

const ConfigSystemMgmt = () => {
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null as any);
  const queryClient = useQueryClient();

  const isUpdate = useCheckPermission(configPermission.UPDATE);
  const isDelete = useCheckPermission(configPermission.DELETE);
  const isCreate = useCheckPermission(configPermission.CREATE);
  const { getColumnSearchProps } = useColumn();

  const onCloseModal = () => {
    setSelectedItem(null);
    setIsModalOpen(false);
  };

  const onEdit = (record: any) => {
    const fields = {
      id: record.id,
      Title: record.Title,
      eSystemCode: record.eSystemCode,
      eSystemType: record.eSystemType,
      eDescription: record.eDescription,
      eTeam: record.eTeam,
      eITHead: record.eITHead,
      eITHeadName: record.eITHeadName,
      eITTeamLead: record.eITTeamLead,
      eTeamLeadName: record.eTeamLeadName,
      eITStaffName: record.eITStaffName,
      eITStaffMail: record.eITStaffMail,
    };
    setSelectedItem(fields);
    setTimeout(() => setIsModalOpen(true), 0);
  };

  const showDeleteConfirm = (id: string) => {
    Modal.confirm({
      title: "Thông báo",
      content: "Anh/chị vui lòng xác nhận xoá dữ liệu đã chọn!",
      okText: "Xác nhận",
      okType: "danger",
      okButtonProps: { type: "primary" },
      cancelText: "Huỷ",
      onOk() {
        onRemove(id);
      },
    });
  };

  const onRemove = async (id: string) => {
    try {
      await SystemMgmtService.deleteSoftware(id);
      CreateNotification({
        title: "Thành công",
        body: "Xoá thông tin phần mềm thành công",
        icon: "/icons/success.png",
      });
      queryClient.invalidateQueries({ queryKey: ["software"] });
    } catch (error) {}
  };

  const onSubmit = (values: any) => {
    handleAdd(values);
    onCloseModal();
  };

  const getSoftwares = async () => {
    try {
      const res = await SystemMgmtService.getSoftwares();
      const softwares = res.map((e: ISoftware) => {
        return {
          ...e,
        };
      });
      return softwares;
    } catch (error) {
      console.log(error);
    }
  };

  const columns = [
    {
      key: "action",
      dataIndex: "action",
      width: 30,
      align: "left" as const,
      render: (_: string, record: any) => (
        <Space>
          {isUpdate && (
            <Tooltip title="Chỉnh sửa">
              <HiPencilSquare size={20} className="cursor-pointer" color="#0ea5e9" onClick={() => onEdit(record)} />
            </Tooltip>
          )}
          {isDelete && (
            <Tooltip title="Xoá">
              <HiOutlineTrash
                size={20}
                className="cursor-pointer"
                color="#dc2626"
                onClick={() => showDeleteConfirm(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Mã QT",
      dataIndex: "eSystemCode",
      editable: true,
      ...getColumnSearchProps("eSystemCode", "Nhập mã cần tìm"),
    },
    {
      title: "Loại QT",
      dataIndex: "eSystemType",
      editable: true,
      render: (type: string) => eSystemType.find((e: any) => e.value === type)?.label || "",
    },
    {
      title: "Tên quy trình",
      dataIndex: "Title",
      editable: true,
      ...getColumnSearchProps("Title", "Nhập tên cần tìm"),
    },
    {
      title: "Mô tả",
      dataIndex: "eDescription",
      editable: true,
    },
    {
      title: "Nhóm",
      dataIndex: "eTeam",
      editable: true,
    },
    {
      title: "Email bước 2",
      dataIndex: "eITTeamLead",
      editable: true,
    },
    {
      title: "Họ tên bước 2",
      dataIndex: "eTeamLeadName",
      editable: true,
    },
    {
      title: "Email bước 3",
      dataIndex: "eITHead",
      editable: true,
    },
    {
      title: "Họ tên bước 3",
      dataIndex: "eITHeadName",
      editable: true,
    },
    {
      title: "Email bước 4",
      dataIndex: "eITStaffMail",
      editable: true,
    },
    {
      title: "Họ tên bước 4",
      dataIndex: "eITStaffName",
      editable: true,
    },
  ];

  const handleAdd = async (newData: any) => {
    try {
      let res;
      if (selectedItem) {
        const { id, key, "@odata.etag": _tag, eITStaffMail, eITStaffName, ...newFields } = newData;
        const updateFields = {
          ...newFields,
          eITStaff: eITStaffMail + "|" + eITStaffName,
        };
        res = await SystemMgmtService.updateSoftware(selectedItem.id, updateFields);
        if (res.status === 200) {
          CreateNotification({
            title: "Thành công",
            body: "Cập nhật thông tin quy trình mới thành công",
            icon: "/icons/success.png",
          });
          queryClient.invalidateQueries({ queryKey: ["software"] });
        } else
          CreateNotification({
            title: "Không thể cập nhật",
            body: "Không thể cập nhật quy trình, vui lòng thử lại",
            icon: "/icons/warning.png",
          });
      } else {
        const { key, eITStaffMail, eITStaffName, ...newDataWithoutKey } = newData;
        const insertParams = {
          fields: {
            ...newDataWithoutKey,
            eITStaff: eITStaffMail + "|" + eITStaffName,
          },
        };
        res = await SystemMgmtService.addSoftware(insertParams);
        if (res.status === 201) {
          CreateNotification({
            title: "Thành công",
            body: "Thêm quy trình mới thành công",
            icon: "/icons/success.png",
          });
          queryClient.invalidateQueries({ queryKey: ["software"] });
        } else
          CreateNotification({
            title: "Không thể thêm",
            body: "Không thể thêm quy trình, vui lòng thử lại",
            icon: "/icons/warning.png",
          });
      }
    } catch (error) {}
  };

  const softwares = useQuery({
    queryKey: ["software"],
    queryFn: getSoftwares,
  });

  const dataSource = softwares.data?.map((e: ISoftware) => ({
    eITStaffMail: StringHelper.verticalBarSplit(e.eITStaff)[0],
    eITStaffName: StringHelper.verticalBarSplit(e.eITStaff)[1],
    ...e,
  }));

  const onImportFileChange = async (values: any[] = []) => {
    const insertValues = values.map((e) => {
      const { eITStaffMail, eITStaffName, ...fields } = e;
      return {
        ...fields,
        eITStaff: eITStaffMail + "|" + eITStaffName,
        eSystemType: e.eSystemType === eSystemType[0].label ? eSystemType[0].value : eSystemType[1].value,
      };
    });

    try {
      const promises: any[] = [];

      insertValues.forEach((value: ISoftware) => {
        const insertParams = {
          fields: {
            ...value,
          },
        };
        promises.push(SystemMgmtService.addSoftware(insertParams));
      });

      await Promise.all(promises);
      queryClient.invalidateQueries({ queryKey: ["software"] });
      notification.success({
        message: "Thành công",
        description: "Nhập liệu thành công!",
      });
    } catch (error) {
      notification.error({
        message: "Thất bại",
        description: "Nhập liệu thất bại. Vui lòng kiểm tra lại định dạng dữ liệu",
      });
    }
  };

  return (
    <div>
      <Spin spinning={softwares.isLoading}>
        <Form form={form} component={false}>
          {isCreate && (
            <>
              <Button style={{ margin: "10px", marginLeft: 0 }} variant="primary" onClick={() => setIsModalOpen(true)}>
                Thêm mới
              </Button>
              <UploadExcelFile template="config-software" onChange={onImportFileChange}>
                <Button variant="success">Nhập liệu</Button>
              </UploadExcelFile>
              <DownloadExcelTemplate template="config-software" className="download-excel-btn" />
            </>
          )}
          <Form.Item>
            <Table
              bordered
              dataSource={dataSource}
              columns={columns}
              rowClassName="editable-row"
              style={{ overflowX: "auto" }}
              scroll={{ x: "2000px" }}
            />
          </Form.Item>
          <Modal
            width={1000}
            title={<div style={{ fontSize: 18 }}>{selectedItem ? "Chỉnh sửa quy trình" : "Thêm quy trình mới"}</div>}
            open={isModalOpen}
            footer={null}
            onCancel={onCloseModal}
            destroyOnClose
          >
            <InsertSoftwareForm onSubmit={onSubmit} software={selectedItem} teamOptions={systemTeams} />
          </Modal>
        </Form>
      </Spin>
    </div>
  );
};

export default ConfigSystemMgmt;
